import { Box, Slab } from '../../render/parts'
import { DoorFronts } from '../parts/storage'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Bank of staff lockers: recessed plinth, inset carcass, overhanging top cap and
 * a grid of doors. Each door gets a small latch handle and a thin accent number
 * plate — the only colour note on the unit.
 */

const WIDTH = 1.2
const DEPTH = 0.45
const HEIGHT = 1.8

const PLINTH = 0.1
const CAP = 0.024
/** Carcass inset from the cap, in plan. */
const BODY_W = WIDTH - 0.012
const BODY_D = DEPTH - 0.008

const ROWS = 2
const ROW_GAP = 0.008
/** Door column gap — must match the gap `DoorFronts` uses internally. */
const COL_GAP = 0.006

const DOOR_BOTTOM = PLINTH + 0.01
const DOOR_TOP = HEIGHT - CAP - 0.008
const DOOR_H = (DOOR_TOP - DOOR_BOTTOM - ROW_GAP * (ROWS + 1)) / ROWS
/** Front plane of the door leaves, as laid out by `DoorFronts`. */
const FACE_Z = BODY_D / 2 + 0.008

const rowY = (row: number) => DOOR_BOTTOM + ROW_GAP + DOOR_H / 2 + row * (DOOR_H + ROW_GAP)

/**
 * Latch handle plus number plate for one door. Positioned from the same column
 * maths `DoorFronts` uses, so the fittings sit on the leaves it drew.
 */
function DoorFittings({
  columns,
  accent,
}: {
  columns: number
  accent?: string
}) {
  const leaf = (BODY_W - COL_GAP * (columns + 1)) / columns
  const plateWidth = Math.min(0.11, leaf * 0.4)

  return (
    <group>
      {Array.from({ length: ROWS }, (_, row) =>
        Array.from({ length: columns }, (_, column) => {
          const x = -BODY_W / 2 + COL_GAP + leaf / 2 + column * (leaf + COL_GAP)
          const y = rowY(row)
          return (
            <group key={`${row}:${column}`}>
              {/* Upright latch on the opening edge. */}
              <Box
                tone="metalDark"
                size={[0.014, 0.1, 0.016]}
                position={[x + leaf / 2 - 0.045, y, FACE_Z + 0.012]}
              />
              {/* Number plate. */}
              <Slab
                tone="accent"
                color={accent}
                size={[plateWidth, 0.05, 0.008]}
                radius={0.004}
                rotation={[Math.PI / 2, 0, 0]}
                position={[x - leaf / 2 + plateWidth / 2 + 0.028, y + DOOR_H / 2 - 0.085, FACE_Z + 0.008]}
              />
            </group>
          )
        }),
      )}
    </group>
  )
}

function Lockers({ accent, variant = '4x2' }: AssetContext) {
  const columns = variant === '3x2' ? 3 : 4

  // The carcass dips below the plinth top and stops short of the cap top so no
  // two parts ever finish on the same plane.
  const bodyBottom = PLINTH - 0.008
  const bodyTop = HEIGHT - CAP + 0.008

  return (
    <group>
      {/* Recessed toe plinth. */}
      <Box
        tone="shellSoft"
        size={[WIDTH - 0.08, PLINTH, DEPTH - 0.06]}
        position={[0, PLINTH / 2, 0]}
      />

      {/* Carcass. */}
      <Box
        tone="shell"
        size={[BODY_W, bodyTop - bodyBottom, BODY_D]}
        position={[0, (bodyBottom + bodyTop) / 2, 0]}
      />

      {/* Top cap. */}
      <Slab
        tone="shell"
        size={[WIDTH, DEPTH, CAP]}
        radius={0.008}
        position={[0, HEIGHT - CAP / 2, 0]}
      />

      {Array.from({ length: ROWS }, (_, row) => (
        <DoorFronts
          key={row}
          width={BODY_W}
          height={DOOR_H}
          y={rowY(row)}
          depth={BODY_D}
          count={columns}
          handle="none"
        />
      ))}

      <DoorFittings columns={columns} accent={accent} />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'lockers',
  name: 'Casilleros',
  category: 'storage',
  footprint: [WIDTH, DEPTH],
  height: HEIGHT,
  surfaceHeight: HEIGHT,
  accentable: true,
  variants: [
    { id: '4x2', name: '4x2' },
    { id: '3x2', name: '3x2' },
  ],
  keywords: [
    'casilleros',
    'taquillas',
    'lockers',
    'vestuario',
    'almacenaje',
    'storage',
    'staff lockers',
  ],
  Component: Lockers,
}
